import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Card,
  Col,
  Descriptions,
  Skeleton,
  Space,
  Tag,
  Timeline,
  Typography,
} from "antd";

import dayjs from "dayjs";
import { DhlTrackingResponse } from "../../models/Tracking";
const { Text } = Typography;

const getStatusColor = (statusCode: string) => {
  switch (statusCode) {
    case "delivered":
      return "success";
    case "transit":
      return "processing";
    case "failure":
      return "error";
    default:
      return "default";
  }
};

const getEventIcon = (status: string) => {
  switch (status.toUpperCase()) {
    case "DF":
      return <LoadingOutlined style={{ color: "#1890ff" }} />;
    case "PU":
      return <EnvironmentOutlined style={{ color: "#dd1e94ff" }} />;
    case "PL":
      return <CheckCircleOutlined style={{ color: "#13c2c2" }} />;
    case "AF":
      return <EnvironmentOutlined style={{ color: "#6122c7ff" }} />;
    case "TR":
      return <LoadingOutlined style={{ color: "#096dd9" }} />;
    case "RR":
      return <CheckCircleOutlined style={{ color: "#08979c" }} />;
    case "IC":
      return <InfoCircleOutlined style={{ color: "#d48806" }} />;
    case "UD": // Clearance Event
      return <InfoCircleOutlined style={{ color: "#f5222d" }} />;
    case "OH":
      return <ClockCircleOutlined style={{ color: "#fa541c" }} />;
    case "AR":
      return <EnvironmentOutlined style={{ color: "#c41d7f" }} />;
    case "CR":
      return <InfoCircleOutlined style={{ color: "#faad14" }} />;
    case "WC":
      return <LoadingOutlined style={{ color: "#36cfc9" }} />;
    case "ND":
      return <InfoCircleOutlined style={{ color: "#f5222d" }} />;
    case "OK":
      return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
    default:
      return <InfoCircleOutlined style={{ color: "#bfbfbf" }} />;
  }
};
export interface IDHLTrackingProps {
  error: any;
  isLoading: boolean;
  shipmentData: DhlTrackingResponse | undefined;
}

const DHLTracking = ({ error, isLoading, shipmentData }: IDHLTrackingProps) => {
  return (
    <>
      <Col xs={24} lg={10}>
        {error && (
          <Alert
            message="Lỗi tra cứu"
            description="Không thể tìm thấy thông tin vận đơn. Vui lòng kiểm tra lại mã vận đơn."
            type="error"
            showIcon
            className="mb-4"
          />
        )}

        {isLoading && (
          <Card bordered={false} className="shadow-sm">
            <Skeleton active paragraph={{ rows: 4 }} />
          </Card>
        )}

        {shipmentData?.shipments?.[0] && (
          <Card bordered={false} className="shadow-sm">
            <Descriptions
              title={
                <Space className="mb-4">
                  <Text strong style={{ fontSize: 16 }}>
                    Thông tin vận đơn
                  </Text>
                  <Tag
                    color={getStatusColor(
                      shipmentData.shipments[0].status.statusCode.toLowerCase()
                    )}
                  >
                    {shipmentData.shipments[0].status.statusCode}
                  </Tag>
                </Space>
              }
              column={1}
              bordered
              size="small"
              labelStyle={{ fontWeight: 500 }}
            >
              <Descriptions.Item label="Mã vận đơn">
                <Text copyable>{shipmentData.shipments[0].id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Dịch vụ">
                {shipmentData.shipments[0].service}
              </Descriptions.Item>
              <Descriptions.Item label="Điểm đi">
                <Space>
                  <EnvironmentOutlined className="text-blue-500" />
                  {shipmentData.shipments[0].origin.address.addressLocality}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Điểm đến">
                <Space>
                  <EnvironmentOutlined className="text-red-500" />
                  {
                    shipmentData.shipments[0].destination.address
                      .addressLocality
                  }
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}

        <Card
          className="shadow-sm mt-4 border-gray-200"
          title={
            <Space>
              <InfoCircleOutlined />
              <Text strong>Chú thích trạng thái</Text>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              message="Lưu ý"
              description="Thông tin vận đơn được cung cấp bởi DHL. Vui lòng liên hệ DHL để biết thêm chi tiết."
              type="info"
              showIcon
              className="mb-4"
            />
            <div>
              <Text strong>Trạng thái theo trình tự vận chuyển:</Text>
              <div className="mt-2">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <LoadingOutlined style={{ color: "#1890ff" }} />
                    <Text>Rời kho (Departed Facility)</Text>
                  </Space>
                  <Space>
                    <EnvironmentOutlined style={{ color: "#dd1e94ff" }} />
                    <Text>Đã lấy hàng (Picked Up)</Text>
                  </Space>
                  <Space>
                    <CheckCircleOutlined style={{ color: "#13c2c2" }} />
                    <Text>Đã xử lý (Processed)</Text>
                  </Space>
                  <Space>
                    <EnvironmentOutlined style={{ color: "#6122c7ff" }} />
                    <Text>Đến kho (Arrived at Facility)</Text>
                  </Space>
                  <Space>
                    <LoadingOutlined style={{ color: "#096dd9" }} />
                    <Text>Đang vận chuyển (In Transit)</Text>
                  </Space>
                  <Space>
                    <CheckCircleOutlined style={{ color: "#08979c" }} />
                    <Text>Đã nhận (Received)</Text>
                  </Space>
                  <Space>
                    <InfoCircleOutlined style={{ color: "#d48806" }} />
                    <Text>Đang xử lý hải quan (In Customs)</Text>
                  </Space>
                  <Space>
                    <InfoCircleOutlined style={{ color: "#f5222d" }} />
                    <Text>Chờ thủ tục (Clearance Event)</Text>
                  </Space>
                  <Space>
                    <ClockCircleOutlined style={{ color: "#fa541c" }} />
                    <Text>Tạm giữ (On Hold)</Text>
                  </Space>
                  <Space>
                    <EnvironmentOutlined style={{ color: "#c41d7f" }} />
                    <Text>Đã đến nơi (Arrived)</Text>
                  </Space>
                  <Space>
                    <InfoCircleOutlined style={{ color: "#faad14" }} />
                    <Text>Đã thông quan (Customs Released)</Text>
                  </Space>
                  <Space>
                    <LoadingOutlined style={{ color: "#36cfc9" }} />
                    <Text>Đang giao hàng (With Courier)</Text>
                  </Space>
                  <Space>
                    <InfoCircleOutlined style={{ color: "#f5222d" }} />
                    <Text>Giao thất bại (Not Delivered)</Text>
                  </Space>
                  <Space>
                    <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    <Text>Giao thành công (OK)</Text>
                  </Space>
                </Space>
              </div>
            </div>
          </Space>
        </Card>
      </Col>

      <Col xs={24} lg={14}>
        {shipmentData?.shipments?.[0] && (
          <Card
            bordered={false}
            className="shadow-sm h-full"
            title={
              <Space>
                <ClockCircleOutlined />
                <Text strong>Lịch sử vận chuyển</Text>
              </Space>
            }
          >
            <Timeline
              pendingDot
              mode="left"
              items={shipmentData.shipments[0].events.map((event, index) => ({
                dot: getEventIcon(event.status.toLowerCase()),
                children: (
                  <div className="pb-6">
                    <Text strong className="block text-base">
                      {event.description}
                    </Text>
                    <Space className="mt-2">
                      <Tag icon={<ClockCircleOutlined />}>
                        {dayjs(event.timestamp).format("DD/MM/YYYY HH:mm")}
                      </Tag>
                      <Tag icon={<EnvironmentOutlined />}>
                        {event.location.address.addressLocality},{" "}
                        {event.location.address.countryCode}
                      </Tag>
                    </Space>
                  </div>
                ),
              }))}
            />
          </Card>
        )}
      </Col>
    </>
  );
};

export default DHLTracking;
